import deepEqual from 'deep-equal'
import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core'
import { PermissionModel } from 'main/permissionModel'
import { APIService } from 'api/module'
import { BaseComponent } from 'common/helpers'
import { MediaRootDetail, MediaRootPermission, SharingPermissionPreset } from 'elements-sdk'
import { MediaPermissionsService } from 'media/services/mediaPermissions.service'
import { IdentityService } from 'main/services/identity.service'
import { debounceTime } from 'rxjs/operators'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'

@Component({
  selector: 'media-permission-editor',
  templateUrl: './mediaPermissionEditor.component.pug',
  styleUrls: ['./mediaPermissionEditor.component.scss'],
})
export class MediaPermissionEditorComponent<T extends MediaRootPermission|SharingPermissionPreset> extends BaseComponent {
  @Input() presetMode = false
  @Input() maxPermission?: MediaRootPermission
  @Input() root?: MediaRootDetail
  @Input() permission: T
  @Output() permissionChange = new EventEmitter<T>()

  permissionPresets: SharingPermissionPreset[]|null = null
  enabledPresets: SharingPermissionPreset[] = []

  schema = this.mediaPermissions.schema
  permissionModel = new PermissionModel(this.schema)

  private defaultsAlreadyEnabled = false

  constructor (
    private mediaPermissions: MediaPermissionsService,
    private identity: IdentityService,
    private api: APIService,
    private ngbModal: NgbModal,
  ) {
    super()
    this.subs.add(
      this.permissionModel.valueChange$
        .pipe(debounceTime(0))
        .subscribe(value => {
          for (const p of this.permissionModel.getAllPermissions()) {
            this.permission[p.id] = value.includes(p.id)
          }
          this.permission = { ...this.permission }
          this.permissionChange.emit(this.permission)
        }),
    )
  }

  async ngOnChanges (changes: SimpleChanges) {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (changes.permission) {
      if (changes.permission.isFirstChange() || !deepEqual(changes.permission.previousValue, changes.permission.currentValue)) {
        this.permissionModel.setValue(this.permissionModel.getAllPermissions()
          .filter(p => this.permission[p.id])
          .map(p => p.id))
        this.permissionModel.blacklist = this.permissionModel.getAllPermissions()
          .filter(p => !this.isPermissionAllowed(p.id))
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (changes.presetMode && this.presetMode && !this.permissionPresets) {
      await this.reloadPresets()
      // Enable default presets
      if (!this.defaultsAlreadyEnabled) {
        this.defaultsAlreadyEnabled = true
        this.enabledPresets = this.permissionPresets!.filter(x => x._default)
        this.updateModelFromPresets()
      }
    }
  }

  updateModelFromPresets () {
    for (const p of this.permissionModel.getAllPermissions()) {
      this.permissionModel.removePermission(p)
    }

    for (const enabledPreset of this.enabledPresets) {
      for (const p of this.permissionModel.getAllPermissions()) {
        if (enabledPreset[p.id] && this.isPermissionAllowed(p.id)) {
          this.permissionModel.addPermission(this.permissionModel.getPermission(p.id)!)
        }
      }
    }
  }

  async reloadPresets () {
    this.permissionPresets = null
    this.permissionPresets = await this.api.mediaLibrary.getAllSharingPermissionPresets({})
  }

  isPermissionVisible (id) {
    return {
      // extra per-permission conditions
      showAiMetadata: !!this.root?.aiConnection,
    }[id] ?? true
  }

  isPermissionAllowed (id) {
    const rootPermit = this.root ? (this.root as object)[id] : true
    return (!this.maxPermission || this.maxPermission[id] || this.identity.hasPermission('media:roots:manage')) && rootPermit !== false
  }

  isPresetAllowed (preset: SharingPermissionPreset) {
    return this.permissionModel.getAllPermissions()
      .filter(p => preset[p.id])
      .every(p => this.isPermissionAllowed(p.id))
  }

  isPresetEnabled (preset: SharingPermissionPreset) {
    return this.enabledPresets.includes(preset)
  }

  togglePreset (preset: SharingPermissionPreset, enable) {
    if (!this.permissionPresets) {
      return
    }
    if (enable) {
      this.enabledPresets.push(preset)
    } else {
      this.enabledPresets = this.enabledPresets.filter(x => x !== preset)
    }
    this.updateModelFromPresets()
  }

  setAllPermissions (group) {
    for (const p of group.items) {
      if (this.isPermissionAllowed(p.id)) {
        this.permissionModel.addPermission(p)
      }
    }
  }

  setNoPermissions (group) {
    for (const p of group.items) {
      this.permissionModel.removePermission(p)
    }
  }

  clickPermission (permission) {
    if (!this.isPermissionAllowed(permission.id)) {
      return
    }
    this.permissionModel.togglePermission(permission)
  }
}
